import { useEffect, useRef, useState } from 'react';
import axios from '@/api/ai-axios';
import oldAxios from 'axios';
import { message } from 'antd';

/**
 * 自定义请求 Hook，用于管理异步请求的 loading 状态和 UI
 * @returns {Object} 包含以下属性的对象：
 *   - loadingStates: 对象，存储每个请求的 loading 状态，key 为请求标识，value 为布尔值
 *   - sendRequest: 函数，发送请求并自动管理 loading 状态
 *     @param {Object} config - 请求配置，支持 axios 的所有配置项
 *     @param {string} [key='default'] - 请求标识，用于区分不同的 loading 状态
 *     @returns {Promise} 返回请求结果的数据
 *     @param {string} [key='default'] - 请求标识，用于指定要渲染的 loading 状态
 *     @param {ReactNode} [customLoading=null] - 自定义 loading UI，如果未传入则使用默认的 Spin 组件
 *     @returns {ReactNode|null} 如果当前请求的 loading 状态为 true，返回 loading UI；否则返回 null
 */

const useRequest = () => {
  const [loadingStates, setLoadingStates] = useState({});
  const [response, setResponse] = useState({
    data: null,
    message: '',
    status: 0,
  });
  const sourceRef = useRef();

  useEffect(() => {
    // 组件卸载时取消所有进行中的请求
    return () => {
      if (sourceRef.current) {
        sourceRef.current.cancel('组件已卸载');
      }
    };
  }, []);

  const sendRequest = async (config) => {
    // 创建新的 CancelToken
    const CancelToken = oldAxios.CancelToken;
    const source = CancelToken.source();
    sourceRef.current = source;

    try {
      setLoadingStates((state) => ({ ...state, [config.url]: true }));
      const newResponse = await axios({ ...config, cancelToken: source.token });
      setResponse(newResponse);
      if (newResponse.status < 1) {
        message.error(
          newResponse.message || config.url.split('/').pop() + '请求失败',
        );
      }
      setLoadingStates((state) => ({ ...state, [config.url]: false }));
      return newResponse;
    } catch (error) {
      if (!axios.isCancel(error)) {
        setResponse({ data: null, message: error.message, status: 0 });
        setLoadingStates((state) => ({ ...state, [config.url]: false }));
        console.error('请求失败:', error);
        message.error(
          config.url.split('/').pop() + '请求失败:' + error.message,
        );
      }
      return { data: null, message: error.message, status: 0 };
    }
  };

  return { loadingStates, response, sendRequest };
};

export default useRequest;
